import { bytesToBase64, base64ToBytes, randomBytes } from './bytes.ts';

const SHARE_PREFIX = 'FARFAR-SSS-1';
export const MAX_SHAMIR_SHARES = 255;

function gfMul(a: number, b: number): number {
  let p = 0;
  for (let i = 0; i < 8; i++) {
    if (b & 1) p ^= a;
    const hi = a & 0x80;
    a = (a << 1) & 0xff;
    if (hi) a ^= 0x1b;
    b >>= 1;
  }
  return p;
}

function gfPow(a: number, exp: number): number {
  let result = 1;
  let base = a;
  let e = exp;
  while (e > 0) {
    if (e & 1) result = gfMul(result, base);
    base = gfMul(base, base);
    e >>= 1;
  }
  return result;
}

function gfInv(a: number): number {
  if (a === 0) throw new Error('Cannot invert 0 in GF(256)');
  return gfPow(a, 254);
}

export type ShamirShare = {
  line: string;
  x: number;
  shortName: string;
  label: string;
};

export type ShamirByteShare = {
  index: number;
  value: Uint8Array;
};

function isUint8Array(value: unknown): value is Uint8Array {
  return (
    ArrayBuffer.isView(value) &&
    Object.prototype.toString.call(value) === '[object Uint8Array]'
  );
}

function evalPoly(coeffs: Uint8Array, x: number): number {
  let y = 0;
  let xPow = 1;
  for (let i = 0; i < coeffs.length; i++) {
    y ^= gfMul(coeffs[i], xPow);
    xPow = gfMul(xPow, x);
  }
  return y;
}

function interpolateAtZero(points: { x: number; y: number }[]): number {
  let secret = 0;
  for (let i = 0; i < points.length; i++) {
    let li = 1;
    for (let j = 0; j < points.length; j++) {
      if (i === j) continue;
      const num = points[j].x;
      const den = points[i].x ^ points[j].x;
      li = gfMul(li, gfMul(num, gfInv(den)));
    }
    secret ^= gfMul(points[i].y, li);
  }
  return secret;
}

export function validateShamirParameters(n: number, k: number): void {
  if (!Number.isInteger(n) || n < 2 || n > MAX_SHAMIR_SHARES) {
    throw new Error(`Shamir share count must be an integer from 2 to ${MAX_SHAMIR_SHARES}`);
  }
  if (!Number.isInteger(k) || k < 2 || k > n) {
    throw new Error('Shamir threshold must be an integer from 2 to the share count');
  }
}

export function validateShamirByteShares(shares: readonly ShamirByteShare[]): number {
  if (!Array.isArray(shares) || shares.length === 0 || shares.length > MAX_SHAMIR_SHARES) {
    throw new Error(`Need between 1 and ${MAX_SHAMIR_SHARES} Shamir shares`);
  }

  const indexes = new Set<number>();
  let byteLength = -1;
  for (const share of shares) {
    if (!share || !Number.isInteger(share.index) || share.index < 1 || share.index > MAX_SHAMIR_SHARES) {
      throw new Error(`Shamir share indexes must be integers from 1 to ${MAX_SHAMIR_SHARES}`);
    }
    if (indexes.has(share.index)) {
      throw new Error('Duplicate share index');
    }
    indexes.add(share.index);
    if (!isUint8Array(share.value) || share.value.length === 0) {
      throw new Error('Shamir share values must be non-empty byte arrays');
    }
    if (byteLength === -1) byteLength = share.value.length;
    if (share.value.length !== byteLength) {
      throw new Error('Share lengths do not match');
    }
  }
  return byteLength;
}

export function splitSecretBytes(
  secret: Uint8Array,
  n: number,
  k: number,
  requestedIndexes?: readonly number[]
): ShamirByteShare[] {
  validateShamirParameters(n, k);
  if (!isUint8Array(secret) || secret.length === 0) {
    throw new Error('Shamir secret must be a non-empty byte array');
  }
  const indexes =
    requestedIndexes || Array.from({ length: n }, (_, index) => index + 1);
  if (!Array.isArray(indexes) || indexes.length !== n) {
    throw new Error('Need one Shamir index per share');
  }

  const uniqueIndexes = new Set<number>();
  for (const index of indexes) {
    if (!Number.isInteger(index) || index < 1 || index > MAX_SHAMIR_SHARES) {
      throw new Error(`Shamir share indexes must be integers from 1 to ${MAX_SHAMIR_SHARES}`);
    }
    if (uniqueIndexes.has(index)) throw new Error('Duplicate share index');
    uniqueIndexes.add(index);
  }

  const values = Array.from({ length: n }, () => new Uint8Array(secret.length));
  for (let byteIndex = 0; byteIndex < secret.length; byteIndex++) {
    const coeffs = new Uint8Array(k);
    const randomCoefficients = randomBytes(k - 1);
    try {
      coeffs[0] = secret[byteIndex];
      coeffs.set(randomCoefficients, 1);
      for (let shareIndex = 0; shareIndex < n; shareIndex++) {
        values[shareIndex][byteIndex] = evalPoly(coeffs, indexes[shareIndex]);
      }
    } finally {
      coeffs.fill(0);
      randomCoefficients.fill(0);
    }
  }

  return values.map((value, index) => ({ index: indexes[index], value }));
}

export function combineSecretBytes(
  shares: readonly ShamirByteShare[],
  threshold: number
): Uint8Array {
  if (!Number.isInteger(threshold) || threshold < 2 || threshold > MAX_SHAMIR_SHARES) {
    throw new Error(`Shamir threshold must be an integer from 2 to ${MAX_SHAMIR_SHARES}`);
  }
  const byteLength = validateShamirByteShares(shares);
  if (shares.length < threshold) {
    throw new Error(`Need at least ${threshold} Shamir shares`);
  }

  const secret = new Uint8Array(byteLength);
  for (let byteIndex = 0; byteIndex < byteLength; byteIndex++) {
    secret[byteIndex] = interpolateAtZero(
      shares.map((share) => ({ x: share.index, y: share.value[byteIndex] }))
    );
  }
  return secret;
}

export function splitSecret(
  secret: Uint8Array,
  n: number,
  k: number,
  shortName: string,
  labels: string[]
): ShamirShare[] {
  if (labels.length !== n) {
    throw new Error('Need one label per share');
  }
  return splitSecretBytes(secret, n, k).map(({ index: x, value }, i) => {
    try {
      const label = labels[i].replace(/\|/g, '/');
      const encoded = bytesToBase64(value);
      const line = [SHARE_PREFIX, shortName, String(x), label, encoded].join('|');
      return { line, x, shortName, label: labels[i] };
    } finally {
      value.fill(0);
    }
  });
}

export function parseShareLine(line: string): { shortName: string; x: number; label: string; y: Uint8Array } {
  const parts = line.trim().split('|');
  if (parts.length !== 5 || parts[0] !== SHARE_PREFIX) {
    throw new Error('Not a Farfar Shamir share');
  }
  const shortName = parts[1];
  const x = Number(parts[2]);
  const label = parts[3];
  const y = base64ToBytes(parts[4]);
  if (!shortName || !Number.isInteger(x) || x < 1 || x > MAX_SHAMIR_SHARES || y.length === 0) {
    throw new Error('Malformed Shamir share');
  }
  return { shortName, x, label, y };
}

export function combineShares(lines: string[]): Uint8Array {
  const parsed = lines.map(parseShareLine);
  try {
    if (parsed.length < 2) throw new Error('Need at least two shares');
    const names = new Set(parsed.map((p) => p.shortName));
    if (names.size !== 1) throw new Error('Shares belong to different people');
    return combineSecretBytes(
      parsed.map((share) => ({ index: share.x, value: share.y })),
      2
    );
  } finally {
    for (const share of parsed) share.y.fill(0);
  }
}
